import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  ImagePreview,
  ImageUpload,
  SideBarModal,
  Status,
} from '@/components';
import dayjs from 'dayjs';
import { Model } from '@/types/common';
import { CreateModelRequest } from '@/services/api/models';
import { getListOem } from '@/services/api/oems';

// 512x512 is a recommendation, not a hard rule — ImageUpload warns on off-spec
// dimensions rather than rejecting them.
const RECOMMENDED_PHOTO_SIZE = 512;

// The shared upload service only accepts a fixed set of image types; a model
// photo is a vehicle photo as far as the temp bucket is concerned. The BE moves
// the object into `<modelID>/` on save, so this only affects the temp folder.
const MODEL_PHOTO_UPLOAD_TYPE = 'vehicle';

interface AddModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateModelRequest) => void;
  isLoading?: boolean;
  initialData?: Model | null;
  isEditMode?: boolean;
}

// Utility component for field display
const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
  hasBorder?: boolean;
  labelClassName?: string;
}> = ({
  label,
  value,
  className = '',
  hasBorder = true,
  labelClassName = '',
}) => (
  <div className={`pb-0 ${className}`}>
    <label className={`block mb-1 ${labelClassName || 'heading-field'}`}>
      {label}
    </label>
    <div
      className={`text-sm font-medium text-gray-900 break-words pb-3 ${hasBorder ? 'border-b border-gray-200' : ''}`}
    >
      {value || '-'}
    </div>
  </div>
);

// Section Header
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-1">
    <span className="font-medium rounded-t-lg heading-field tracking-wider">
      {title}
    </span>
  </div>
);

const AddModelModal: React.FC<AddModelModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData = null,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<CreateModelRequest>({
    oemID: '',
    name: '',
    unitType: 'NEW',
    purchasePrice: 0,
    sellingPrice: 0,
    contractType: 'RTO',
    batteryType: 'CHARGING',
    maxDistancePerBattery: 0,
    maxWeight: 0,
    deposit: 0,
    area: '',
    createdBy: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isViewingInfo, setIsViewingInfo] = useState(false);
  const [oems, setOems] = useState<any[]>([]);
  const [depositTouched, setDepositTouched] = useState(false);

  // Blocks submit while the catalog photo is still uploading; ImageUpload owns
  // the rest of the photo state.
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Display strings for IDR fields (formatted with thousand separators)
  const [idrDisplay, setIdrDisplay] = useState({
    purchasePrice: '',
    sellingPrice: '',
    deposit: '',
  });

  const formatIDR = (value: number | string): string => {
    const num =
      typeof value === 'string'
        ? parseFloat(value.replace(/\./g, '').replace(/,/g, '.'))
        : value;
    if (isNaN(num) || num === 0) return '';
    return num.toLocaleString('id-ID');
  };

  const handleIDRChange = (
    field: 'purchasePrice' | 'sellingPrice' | 'deposit',
    raw: string
  ) => {
    // Allow only digits and dots (thousand separators)
    const digitsOnly = raw.replace(/[^\d]/g, '');
    const numValue = parseInt(digitsOnly, 10) || 0;
    const formatted = numValue === 0 ? '' : numValue.toLocaleString('id-ID');
    setIdrDisplay((prev) => ({ ...prev, [field]: formatted }));
    handleInputChange(field, numValue);
    if (field === 'deposit') {
      setDepositTouched(numValue > 0 || digitsOnly !== '');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const oemsResponse = await getListOem({ page: '1', size: '1000' });
        setOems(oemsResponse.data || []);
      } catch (error) {}
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isEditMode && initialData) {
      setIsViewingInfo(true);
      setFormData({
        oemID: initialData.oemID || '',
        name: initialData.name || '',
        unitType: initialData.unitType || 'NEW',
        purchasePrice: initialData.purchasePrice || 0,
        sellingPrice: initialData.sellingPrice || 0,
        contractType: initialData.contractType || 'RTO',
        batteryType: initialData.batteryType || 'CHARGING',
        maxDistancePerBattery: initialData.maxDistancePerBattery || 0,
        maxWeight: initialData.maxWeight || 0,
        deposit: initialData.deposit || 0,
        area: initialData.area || '',
        // Sent back unchanged unless ops replaces or removes the photo — the BE
        // treats an identical URL as "keep as-is".
        imageURL: initialData.imageURL ?? null,
        createdBy: '',
      });
      setIdrDisplay({
        purchasePrice: formatIDR(initialData.purchasePrice || 0),
        sellingPrice: formatIDR(initialData.sellingPrice || 0),
        deposit: formatIDR(initialData.deposit || 0),
      });
      setDepositTouched(true);
    } else {
      setIsViewingInfo(false);
      setFormData({
        oemID: '',
        name: '',
        unitType: 'NEW',
        purchasePrice: 0,
        sellingPrice: 0,
        contractType: 'RTO',
        batteryType: 'CHARGING',
        maxDistancePerBattery: 0,
        maxWeight: 0,
        deposit: 0,
        area: '',
        createdBy: '',
      });
      setIdrDisplay({ purchasePrice: '', sellingPrice: '', deposit: '' });
      setDepositTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialData, isOpen]);

  const handleInputChange = (
    field: keyof CreateModelRequest,
    value: string | number | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleNumberInputFocus = (field: keyof CreateModelRequest) => {
    if (formData[field] === 0) {
      setFormData((prev) => ({ ...prev, [field]: '' as any }));
    }
  };

  const handleNumberInputBlur = (
    field: keyof CreateModelRequest,
    value: string
  ) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || value === '') {
      setFormData((prev) => ({ ...prev, [field]: 0 }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.oemID) {
      newErrors.oemID = 'OEM is required';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Model Name is required';
    }
    // if (!formData.contractStartDate) {
    //   newErrors.contractStartDate = 'Contract Start Date is required';
    if (formData.purchasePrice <= 0) {
      newErrors.purchasePrice =
        'Purchase Price is required and must be greater than 0';
    }
    if (formData.sellingPrice <= 0) {
      newErrors.sellingPrice =
        'Selling Price is required and must be greater than 0';
    }
    if (formData.maxDistancePerBattery <= 0) {
      newErrors.maxDistancePerBattery = 'Max Distance must be greater than 0';
    }
    if (formData.maxWeight <= 0) {
      newErrors.maxWeight = 'Max Weight must be greater than 0';
    }
    if (!depositTouched) {
      newErrors.deposit = 'Deposit is required';
    } else if (formData.deposit < 0) {
      newErrors.deposit = 'Deposit cannot be negative';
    }
    if (!formData.area.trim()) {
      newErrors.area = 'Area is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      oemID: '',
      name: '',
      unitType: 'NEW',
      purchasePrice: 0,
      sellingPrice: 0,
      contractType: 'RTO',
      batteryType: 'CHARGING',
      maxDistancePerBattery: 0,
      maxWeight: 0,
      deposit: 0,
      area: '',
      createdBy: '',
    });
    setErrors({});
    setIsViewingInfo(false);
    setDepositTouched(false);
    onClose();
  };

  const handleEditClick = () => {
    setIsViewingInfo(false);
  };

  // Reactively check if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      !!formData.oemID &&
      !!formData.name.trim() &&
      formData.purchasePrice > 0 &&
      formData.sellingPrice > 0 &&
      formData.maxDistancePerBattery > 0 &&
      formData.maxWeight > 0 &&
      depositTouched &&
      !!formData.area.trim()
    );
  }, [formData, depositTouched]);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          /* Hide number input arrows */
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>

      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Model' : 'Add Model'}
            </h2>
            <p className="text-sm text-gray-500">
              {isEditMode ? 'Edit model details.' : 'New model details.'}
            </p>
          </div>
        }
        footer={
          isEditMode && isViewingInfo ? (
            <Button
              variant="primary"
              onClick={handleEditClick}
              className="w-full py-3"
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                const isValid = validateForm();
                if (isValid) {
                  onSubmit(formData);
                }
              }}
              disabled={isLoading || isUploadingPhoto || !isFormValid}
              className="w-full py-3"
            >
              {isLoading
                ? 'Saving...'
                : isUploadingPhoto
                  ? 'Uploading photo...'
                  : isEditMode
                    ? 'Update'
                    : 'Save'}
            </Button>
          )
        }
      >
        <div className="flex-1">
          {isEditMode && isViewingInfo ? (
            <>
              <div>
                <SectionHeader title="Model Details" />

                <div className="px-6 pb-0">
                  {/* Photo */}
                  <div className="border-b border-gray-200 pt-5 pb-3">
                    <FieldDisplay
                      label="Foto Model"
                      value={
                        initialData?.imageURL ? (
                          <ImagePreview
                            src={initialData.imageURL}
                            alt={initialData?.name || 'Model'}
                            className="h-32 w-32 rounded-lg object-cover bg-gray-100 border border-gray-200 mt-1"
                          />
                        ) : (
                          'Belum ada foto'
                        )
                      }
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 1 */}
                  <div className="grid grid-cols-2 gap-x-6 border-b border-gray-200 pt-5">
                    <FieldDisplay
                      label="OEM Name"
                      value={initialData?.oemName}
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Model Name"
                      value={initialData?.name}
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 2 */}
                  <div className="grid grid-cols-2 gap-x-6 border-b border-gray-200 pt-3">
                    <FieldDisplay
                      label="Battery Type"
                      value={
                        initialData?.batteryType === 'BOTH'
                          ? 'Swap & Charging'
                          : initialData?.batteryType === 'SWAP'
                            ? 'Swap'
                            : initialData?.batteryType === 'CHARGING'
                              ? 'Charging'
                              : initialData?.batteryType
                      }
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Unit Type"
                      value={
                        initialData?.unitType === 'NEW'
                          ? 'New'
                          : initialData?.unitType === 'SECOND'
                            ? 'Second'
                            : initialData?.unitType
                      }
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 3 */}
                  <div className="grid grid-cols-2 gap-x-6 pt-3">
                    <FieldDisplay
                      label="Jarak Tempuh per Battery"
                      value={
                        initialData?.maxDistancePerBattery
                          ? `${initialData.maxDistancePerBattery} km`
                          : undefined
                      }
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Berat Maksimal"
                      value={
                        initialData?.maxWeight
                          ? `${initialData.maxWeight} kg`
                          : undefined
                      }
                      hasBorder={false}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <SectionHeader title="Contract Detail" />

                <div className="px-6 pb-0">
                  {/* Row 1 */}
                  <div className="grid grid-cols-2 gap-x-6 border-b border-gray-200 pt-5">
                    <FieldDisplay
                      label="Contract Type"
                      value={
                        <Status
                          value={initialData?.contractType || ''}
                          type="contract"
                          size="small"
                        />
                      }
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 2 */}
                  <div className="grid grid-cols-2 gap-x-6 border-b border-gray-200 pt-3">
                    <FieldDisplay
                      label="Harga Beli (Harian)"
                      value={`Rp. ${initialData?.purchasePrice?.toLocaleString('id-ID') || '0'}`}
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Harga Jual (Harian)"
                      value={`Rp. ${initialData?.sellingPrice?.toLocaleString('id-ID') || '0'}`}
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 3 */}
                  <div className="grid grid-cols-2 gap-x-6 pt-3">
                    <FieldDisplay
                      label="Deposit to OEM"
                      value={`Rp. ${initialData?.deposit?.toLocaleString('id-ID') || '0'}`}
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Area"
                      value={initialData?.area}
                      hasBorder={false}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <SectionHeader title="Info" />
                <div className="px-6 pb-2">
                  {/* Row 1 */}
                  <div className="grid grid-cols-2 gap-x-6 border-b border-gray-200 pt-5">
                    <FieldDisplay
                      label="Created By"
                      value={initialData?.createdBy}
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Created Date"
                      value={
                        initialData?.createdAt
                          ? dayjs(initialData.createdAt).format(
                              'MMM DD, YYYY HH:mm'
                            )
                          : undefined
                      }
                      hasBorder={false}
                    />
                  </div>
                  {/* Row 2 */}
                  <div className="grid grid-cols-2 gap-x-6 pt-3">
                    <FieldDisplay
                      label="Last Updated By"
                      value={initialData?.updatedBy}
                      hasBorder={false}
                    />
                    <FieldDisplay
                      label="Last Updated"
                      value={
                        initialData?.updatedAt
                          ? dayjs(initialData.updatedAt).format(
                              'MMM DD, YYYY HH:mm'
                            )
                          : undefined
                      }
                      hasBorder={false}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 heading-field">
                  Model Details
                </div>

                <div className="space-y-3 px-4 pb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OEM Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.oemID}
                      onChange={(e) =>
                        handleInputChange('oemID', e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.oemID ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select OEM</option>
                      {oems.map((oem) => (
                        <option key={oem.ID} value={oem.ID}>
                          {oem.name}
                        </option>
                      ))}
                    </select>
                    {errors.oemID && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.oemID}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      placeholder="e.g. Minerva Exc-5"
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Battery Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.batteryType}
                      onChange={(e) =>
                        handleInputChange(
                          'batteryType',
                          e.target.value as
                            | 'CHARGING'
                            | 'SWAP'
                            | 'CHARGING & SWAP'
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CHARGING">Charging</option>
                      <option value="SWAP">Swap</option>
                      <option value="BOTH">Swap & Charging</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="unitType"
                          value="NEW"
                          checked={formData.unitType === 'NEW'}
                          onChange={(e) =>
                            handleInputChange('unitType', e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">New</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="unitType"
                          value="SECOND"
                          checked={formData.unitType === 'SECOND'}
                          onChange={(e) =>
                            handleInputChange('unitType', e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Second
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Jarak Tempuh per Battery{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={
                          formData.maxDistancePerBattery === 0
                            ? ''
                            : formData.maxDistancePerBattery
                        }
                        onFocus={() =>
                          handleNumberInputFocus('maxDistancePerBattery')
                        }
                        onBlur={(e) =>
                          handleNumberInputBlur(
                            'maxDistancePerBattery',
                            e.target.value
                          )
                        }
                        onChange={(e) =>
                          handleInputChange(
                            'maxDistancePerBattery',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="e.g. 75"
                        className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.maxDistancePerBattery
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      <span className="absolute right-3 top-2 text-sm text-gray-500">
                        km
                      </span>
                    </div>
                    {errors.maxDistancePerBattery && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.maxDistancePerBattery}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Berat Maksimal <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={
                          formData.maxWeight === 0 ? '' : formData.maxWeight
                        }
                        onFocus={() => handleNumberInputFocus('maxWeight')}
                        onBlur={(e) =>
                          handleNumberInputBlur('maxWeight', e.target.value)
                        }
                        onChange={(e) =>
                          handleInputChange(
                            'maxWeight',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="e.g. 250"
                        className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.maxWeight
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      <span className="absolute right-3 top-2 text-sm text-gray-500">
                        kg
                      </span>
                    </div>
                    {errors.maxWeight && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.maxWeight}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200 heading-field">
                  Foto Model
                </div>

                <div className="space-y-3 px-4 pb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Foto Model{' '}
                      <span className="text-gray-400 font-normal">
                        (opsional)
                      </span>
                    </label>
                    <ImageUpload
                      value={formData.imageURL || ''}
                      title="Add a model photo"
                      imageType={MODEL_PHOTO_UPLOAD_TYPE}
                      recommendedSize={RECOMMENDED_PHOTO_SIZE}
                      onUploaded={(url) => handleInputChange('imageURL', url)}
                      // Explicit null (not '') so the BE can tell "remove it"
                      // from "not sent".
                      onRemove={() => handleInputChange('imageURL', null)}
                      onUploadingChange={setIsUploadingPhoto}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200 heading-field">
                  Contract Detail
                </div>

                <div className="space-y-3 px-4 pb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contractType"
                          value="RTO"
                          checked={formData.contractType === 'RTO'}
                          onChange={(e) =>
                            handleInputChange('contractType', e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Rent to Own
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contractType"
                          value="RENT"
                          checked={formData.contractType === 'RENT'}
                          onChange={(e) =>
                            handleInputChange('contractType', e.target.value)
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Rent</span>
                      </label>
                    </div>
                  </div>

                  {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contract Duration{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          value={
                            formData.contractDuration === 0
                              ? ''
                              : formData.contractDuration
                          }
                          onFocus={() =>
                            handleNumberInputFocus('contractDuration')
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              'contractDuration',
                              e.target.value
                            )
                          }
                          onChange={(e) =>
                            handleInputChange(
                              'contractDuration',
                              parseInt(e.target.value) || 0
                            )
                          }
                          placeholder="0"
                          className={`w-full px-3 py-2 pr-16 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.contractDuration
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        <span className="absolute right-3 top-2 text-sm text-gray-500">
                          Months
                        </span>
                      </div>
                      {errors.contractDuration && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.contractDuration}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Contract <span className="text-red-500">*</span>
                      </label>
                      <div
                        className={
                          errors.contractStartDate
                            ? 'border-red-500 rounded-md'
                            : ''
                        }
                      >
                        <DatePicker
                          value={formData.contractStartDate}
                          onChange={(value: string) =>
                            handleInputChange('contractStartDate', value)
                          }
                          placeholder="Select contract start date"
                          showTimeSelect={false}
                          error={!!errors.contractStartDate}
                          helperText={errors.contractStartDate}
                          className="w-full"
                        />
                      </div>
                      {errors.contractStartDate && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.contractStartDate}
                        </p>
                      )}
                    </div> */}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Harga Beli <span className="text-red-500">*</span>{' '}
                      (Harian)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">Rp</span>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={idrDisplay.purchasePrice}
                        onChange={(e) =>
                          handleIDRChange('purchasePrice', e.target.value)
                        }
                        placeholder="50.000"
                        className={`w-full border rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                          errors.purchasePrice
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                        <img
                          src="https://flagcdn.com/w40/id.png"
                          alt="IDR"
                          className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                        />
                        <span className="text-gray-500 text-sm">IDR</span>
                      </div>
                    </div>
                    {errors.purchasePrice && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.purchasePrice}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Harga Jual
                      <span className="text-red-500">*</span> (Harian)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">Rp</span>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={idrDisplay.sellingPrice}
                        onChange={(e) =>
                          handleIDRChange('sellingPrice', e.target.value)
                        }
                        placeholder="65.000"
                        className={`w-full border rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                          errors.sellingPrice
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                        <img
                          src="https://flagcdn.com/w40/id.png"
                          alt="IDR"
                          className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                        />
                        <span className="text-gray-500 text-sm">IDR</span>
                      </div>
                    </div>
                    {errors.sellingPrice && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.sellingPrice}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deposit to OEM <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">Rp</span>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={idrDisplay.deposit}
                        onChange={(e) =>
                          handleIDRChange('deposit', e.target.value)
                        }
                        placeholder="500.000"
                        className={`w-full border rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                          errors.deposit ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                        <img
                          src="https://flagcdn.com/w40/id.png"
                          alt="IDR"
                          className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                        />
                        <span className="text-gray-500 text-sm">IDR</span>
                      </div>
                    </div>
                    {errors.deposit && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.deposit}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Area <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.area}
                      onChange={(e) =>
                        handleInputChange('area', e.target.value)
                      }
                      placeholder="e.g. Jabodetabek"
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.area ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.area && (
                      <p className="text-red-500 text-xs mt-1">{errors.area}</p>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </SideBarModal>
    </>
  );
};

export default AddModelModal;
